import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import type { HeaderIntroProps, HeaderIntroRef } from './HeaderIntro.data';
import styles from './HeaderIntro.module.scss';
import { setupTransitionInTimeline } from './HeaderIntro.transitions';
import LogoIntroSection from './logo-intro-section/LogoIntroSection';
import type { LogoIntroSectionRef } from './logo-intro-section/LogoIntroSection.data';
import HeaderRotatingShape from 'components/blocks/header-rotating-shape/HeaderRotatingShape';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import useScrollTrigger from '@/hooks/useScrollTrigger';

export default function HeaderIntro({
  canTransitionIn,
  ref,
  blok,
  ...props
}: HeaderIntroProps & { ref?: Ref<HeaderIntroRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const innerContentRef = useRef<HTMLDivElement>(null);
  const logoIntroSectionRef = useRef<LogoIntroSectionRef>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        logoIntroSectionRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useThemeScrollBlock(logoIntroSectionRef, blok.use_accent_color, {
    foreground: '#fff',
    background: '#000',
  });

  useThemeScrollBlock(innerContentRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground.color,
    background: blok.accent_color_background.color,
  });

  useScrollTrigger({
    trigger: headerRef,
    pin: headerRef,
    vars: {
      scrub: true,
      start: 'top top',
      end: 'bottom top',
      pinType: 'transform',
      anticipatePin: 1,
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.headerIntro, props.className)}
      {...sbEditable(blok)}
    >
      <LogoIntroSection title={blok?.title} kanjiText={blok?.kanjiText} ref={logoIntroSectionRef} />

      <div className={classNames(styles.content)}>
        <div ref={innerContentRef} className={classNames(styles.innerContent)}>
          <HeaderRotatingShape
            ref={headerRef}
            className={styles.header}
            disableScrollTrigger
            style={
              {
                '--container-min-height': 'calc(var(--vh) * 100)',
              } as React.CSSProperties
            }
            // @ts-ignore
            blok={{
              topTitle: blok?.topLeftTitle,
              bottomTitle: blok?.bottomRightTitle,
              icon: 'pinwheel',
              ...blok,
            }}
          />
        </div>
      </div>
    </div>
  );
}
